import { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Download, Server, Network, Lock, Copy, RefreshCw, Activity, CircleDot } from "lucide-react";

const EVENT_COLOR = {
  Dial: "text-blue-400",
  DialEnd: "text-blue-300",
  NewChannel: "text-sky-400",
  Hangup: "text-zinc-400",
  HangupRequest: "text-zinc-400",
  ContactStatus: "text-emerald-400",
  PeerStatus: "text-emerald-400",
  RegistrationStatus: "text-emerald-400",
  FullyBooted: "text-violet-400",
  Reload: "text-violet-400",
  Shutdown: "text-red-400",
  Hold: "text-amber-400",
  Unhold: "text-amber-300",
  Warning: "text-orange-400",
  Error: "text-red-500",
  _Error: "text-red-500",
};

function eventSummary(pkt) {
  const e = pkt.Event || "";
  if (e === "Dial") return `${pkt.CallerIDNum || "?"} → ${pkt.Exten || pkt.DestExten || "?"}`;
  if (e === "Hangup") return `${pkt.CallerIDNum || pkt.Channel || "?"} — ${pkt.Cause || ""} ${pkt["Cause-txt"] || ""}`.trim();
  if (e === "NewChannel") return `${pkt.Channel || ""} ${pkt.CallerIDNum || ""}`.trim();
  if (e === "ContactStatus") return `${pkt.URI || pkt.AOR || ""} — ${pkt.ContactStatus || ""}`;
  if (e === "PeerStatus") return `${pkt.Peer || ""} — ${pkt.PeerStatus || ""}`;
  if (e === "FullyBooted") return pkt.Status || "Démarré";
  if (e === "Reload") return pkt.Module || "reload";
  if (e === "_Error") return pkt.Message || "Erreur";
  const parts = Object.entries(pkt)
    .filter(([k]) => !["Event","Timestamp","Privilege","ActionID","SequenceNumber"].includes(k))
    .slice(0, 3)
    .map(([k, v]) => `${k}: ${v}`);
  return parts.join("  ");
}

function AsteriskLogs({ servers }) {
  const [serverId, setServerId] = useState("");
  const [connected, setConnected] = useState(false);
  const [logs, setLogs] = useState([]);
  const esRef = useRef(null);
  const bottomRef = useRef(null);
  const API = process.env.REACT_APP_BACKEND_URL || "";

  const connect = () => {
    if (esRef.current) { esRef.current.close(); esRef.current = null; }
    if (!serverId) return;
    const es = new EventSource(`${API}/api/servers/${serverId}/events/stream`, { withCredentials: true });
    esRef.current = es;
    setLogs([]);
    setConnected(true);
    es.onmessage = (ev) => {
      try {
        const pkt = JSON.parse(ev.data);
        if (pkt.Event === "_Error") {
          toast.error(pkt.Message || "Erreur AMI");
          setConnected(false);
          return;
        }
        const ts = new Date().toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit", second: "2-digit" });
        setLogs(prev => [...prev.slice(-499), { ts, pkt }]);
      } catch {}
    };
    es.onerror = () => { setConnected(false); es.close(); };
  };

  const disconnect = () => {
    if (esRef.current) { esRef.current.close(); esRef.current = null; }
    setConnected(false);
  };

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [logs]);
  useEffect(() => () => esRef.current?.close(), []);

  return (
    <section data-testid="asterisk-live-logs" className="mt-8">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="font-head text-lg font-bold tracking-tight text-zinc-900 flex items-center gap-2">
            <Activity className="h-4 w-4" /> Logs Asterisk temps réel
          </h2>
          <p className="text-xs text-zinc-500">Événements AMI en direct (appels, enregistrements SIP, système).</p>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={serverId}
            onChange={e => { setServerId(e.target.value); setConnected(false); disconnect(); }}
            className="rounded-sm border border-zinc-200 bg-white px-3 py-1.5 text-sm"
          >
            <option value="">— Serveur —</option>
            {servers.map(s => <option key={s.id} value={s.id}>{s.name} ({s.ip})</option>)}
          </select>
          {!connected ? (
            <button
              data-testid="ami-connect-btn"
              onClick={connect}
              disabled={!serverId}
              className="inline-flex items-center gap-1.5 rounded-sm bg-zinc-900 px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-40 hover:bg-zinc-700"
            >
              <CircleDot className="h-3.5 w-3.5" /> Connecter
            </button>
          ) : (
            <button
              data-testid="ami-disconnect-btn"
              onClick={disconnect}
              className="inline-flex items-center gap-1.5 rounded-sm border border-red-300 bg-white px-3 py-1.5 text-xs font-semibold text-red-600 hover:bg-red-50"
            >
              Déconnecter
            </button>
          )}
          {logs.length > 0 && (
            <button onClick={() => setLogs([])} className="text-xs text-zinc-400 hover:text-zinc-700">Vider</button>
          )}
        </div>
      </div>

      <div className="mt-3 h-80 overflow-y-auto rounded-sm border border-zinc-800 bg-zinc-950 p-3 font-mono text-xs">
        {logs.length === 0 && (
          <span className="text-zinc-500">{connected ? "En attente d'événements…" : "Sélectionner un serveur et connecter."}</span>
        )}
        {logs.map((entry, i) => {
          const color = EVENT_COLOR[entry.pkt.Event] || "text-zinc-300";
          return (
            <div key={i} className="flex gap-3 leading-5 hover:bg-zinc-900 px-1 rounded">
              <span className="shrink-0 text-zinc-500">{entry.ts}</span>
              <span className={`shrink-0 w-36 truncate font-semibold ${color}`}>{entry.pkt.Event || "?"}</span>
              <span className="text-zinc-300 truncate">{eventSummary(entry.pkt)}</span>
            </div>
          );
        })}
        <div ref={bottomRef} />
      </div>
    </section>
  );
}

/* Ressources regroupées par étape du déploiement (lisible pour un DSI). */
const GROUPS = [
  {
    id: "server",
    step: "1",
    icon: Server,
    title: "Serveur SIP (Asterisk)",
    desc: "Installer un serveur Asterisk durci rattaché à HotelSIP : comptes SIP des chambres, trunk 3CX, agent de provisioning.",
    items: [
      { key: "script", title: "install_asterisk.sh", desc: "Installation durcie (Asterisk 21, AMI, agent HTTPS, ports non standards, allowlist IP). Build depuis les sources." },
      { key: "agent", title: "asterisk_agent.py", desc: "Agent de provisioning (HTTPS + jeton + allowlist IP) embarqué par le script." },
      { key: "setip", title: "set_hotelsip_ip.sh", desc: "Met à jour l'IP autorisée (AMI + agent + firewall) après l'installation." },
      { key: "guide", title: "INSTALL_ASTERISK.md", desc: "Guide d'installation et de raccordement pas à pas." },
      { key: "runbook", title: "PRODUCTION_RUNBOOK.md", desc: "Runbook de validation en production (push, sync, CDR, santé)." },
    ],
  },
  {
    id: "3cx",
    step: "2",
    icon: Network,
    title: "Intégration 3CX",
    desc: "Relier le PBX 3CX de l'hôtel à l'Asterisk par un trunk SIP : touches type 9, appels entrants vers les chambres, nom d'appelant.",
    items: [
      { key: "trunk3cx", title: "HotelSIP-Asterisk-Trunk-V20.xml", desc: "Modèle de trunk 3CX V20 (IP-based) à importer — affichage « Chambre 201 » intégré." },
      { key: "guide3cx", title: "3CX_V20_SETUP.md", desc: "Procédure : import du modèle, trunk, règles entrantes / sortantes." },
    ],
  },
  {
    id: "security",
    step: "3",
    icon: Lock,
    title: "Sécurité",
    desc: "Modèle de sécurité et durcissement de l'infrastructure.",
    items: [
      { key: "security", title: "SECURITY.md", desc: "Ports, allowlist IP, HTTPS, AMI, et sécurisation SIP (TLS/SRTP, fail2ban) en standby." },
    ],
  },
];

const STEPS = [
  { n: "1", t: "Installer le serveur", d: "Exécuter install_asterisk.sh sur un VPS Debian/Ubuntu dédié à l'hôtel." },
  { n: "2", t: "Déclarer dans HotelSIP", d: "Renseigner l'IP, l'AMI et l'agent dans la fiche serveur, puis pousser la configuration." },
  { n: "3", t: "Connecter le 3CX", d: "Importer le modèle de trunk V20 et créer les règles d'appel entrantes / sortantes." },
];

const PORTS = [
  ["SIP (UDP/TCP)", "6060"],
  ["AMI (manager)", "5938"],
  ["Agent (HTTPS)", "9443"],
  ["RTP (média)", "10000-20000"],
];

export default function Deploy() {
  const [logs, setLogs] = useState([]);
  const [logsMsg, setLogsMsg] = useState("");
  const [servers, setServers] = useState([]);

  useEffect(() => {
    api.get("/servers").then(r => setServers(r.data)).catch(() => {});
  }, []);

  const loadLogs = async () => {
    try {
      const { data } = await api.get("/provisioning/logs/tftp");
      setLogs(data.events || []);
      setLogsMsg(data.ok ? "" : "TFTP indisponible sur ce serveur");
    } catch {
      setLogsMsg("Lecture des logs impossible");
    }
  };
  useEffect(() => {
    loadLogs();
  }, []);

  const download = async (key, filename) => {
    try {
      const { data } = await api.get(`/deploy/${key}`);
      const blob = new Blob([data.content], { type: "text/plain" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = data.filename || filename;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`${data.filename} téléchargé`);
    } catch {
      toast.error("Téléchargement impossible");
    }
  };

  const quickInstall = `scp asterisk_agent.py install_asterisk.sh set_hotelsip_ip.sh root@SERVEUR:/root/
ssh root@SERVEUR
HOTELSIP_IP='IP_HOTELSIP' AMI_USER=hotelsip AMI_SECRET='SecretAMI' \\
  AGENT_TOKEN='JetonAgent' bash install_asterisk.sh`;

  return (
    <div data-testid="deploy-page" className="pb-12">
      <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-500">Infrastructure</p>
      <h1 className="mt-1 font-head text-3xl font-black tracking-tighter text-zinc-900">
        Déploiement serveur SIP
      </h1>
      <p className="mt-1 max-w-2xl text-sm text-zinc-500">
        Provisionner l'infrastructure téléphonique d'un hôtel. Chaque hôtel dispose d'un serveur
        Asterisk dédié (chambres + trunk 3CX) piloté à distance par HotelSIP.
      </p>

      {/* Vue d'ensemble : flux + ports */}
      <div className="mt-6 grid gap-4 lg:grid-cols-3">
        <div className="lg:col-span-2 rounded-sm border border-zinc-200 bg-white p-5">
          <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">Déroulé</div>
          <ol className="mt-4 space-y-4">
            {STEPS.map((s) => (
              <li key={s.n} className="flex gap-3">
                <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-sm bg-zinc-900 font-mono text-xs font-bold text-white">
                  {s.n}
                </span>
                <div>
                  <div className="text-sm font-semibold text-zinc-900">{s.t}</div>
                  <div className="text-xs text-zinc-500">{s.d}</div>
                </div>
              </li>
            ))}
          </ol>
        </div>
        <div className="rounded-sm border border-zinc-200 bg-white p-5">
          <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">Ports durcis</div>
          <table className="mt-4 w-full text-sm">
            <tbody>
              {PORTS.map(([name, port]) => (
                <tr key={name} className="border-b border-zinc-100 last:border-0">
                  <td className="py-2 text-zinc-600">{name}</td>
                  <td className="py-2 text-right font-mono font-semibold text-zinc-900">{port}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="mt-3 text-[11px] text-zinc-400">
            AMI &amp; agent restreints à l'IP de HotelSIP (allowlist).
          </p>
        </div>
      </div>

      {/* Sections par étape */}
      {GROUPS.map((g) => (
        <section key={g.id} data-testid={`deploy-group-${g.id}`} className="mt-8">
          <div className="flex items-start gap-3">
            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-sm border border-zinc-200 bg-white">
              <g.icon className="h-4.5 w-4.5 text-zinc-700" />
            </span>
            <div>
              <h2 className="font-head text-lg font-bold tracking-tight text-zinc-900">
                <span className="font-mono text-zinc-400">{g.step} · </span>{g.title}
              </h2>
              <p className="max-w-2xl text-xs text-zinc-500">{g.desc}</p>
            </div>
          </div>

          <div className="mt-4 grid gap-3 md:grid-cols-2 xl:grid-cols-3">
            {g.items.map((r) => (
              <div key={r.key} className="flex flex-col justify-between rounded-sm border border-zinc-200 bg-white p-4">
                <div>
                  <div className="font-mono text-[13px] font-semibold text-zinc-900">{r.title}</div>
                  <p className="mt-1 text-xs leading-relaxed text-zinc-500">{r.desc}</p>
                </div>
                <button
                  data-testid={`download-${r.key}`}
                  onClick={() => download(r.key, r.title)}
                  className="mt-4 inline-flex w-fit items-center gap-1.5 rounded-sm bg-blue-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-blue-700"
                >
                  <Download className="h-3.5 w-3.5" /> Télécharger
                </button>
              </div>
            ))}
          </div>

          {g.id === "server" && (
            <div className="mt-4 overflow-hidden rounded-sm border border-zinc-800 bg-zinc-950">
              <div className="flex items-center justify-between border-b border-zinc-800 px-4 py-2">
                <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">
                  Installation rapide
                </span>
                <button
                  data-testid="copy-quickinstall"
                  onClick={() => {
                    navigator.clipboard.writeText(quickInstall);
                    toast.success("Commande copiée");
                  }}
                  className="inline-flex items-center gap-1 font-mono text-[11px] text-zinc-400 hover:text-white"
                >
                  <Copy className="h-3 w-3" /> Copier
                </button>
              </div>
              <pre className="overflow-auto p-4 font-mono text-[12px] leading-relaxed text-emerald-300">{quickInstall}</pre>
            </div>
          )}
        </section>
      ))}

      {/* Logs de provisioning TFTP */}
      <section data-testid="deploy-tftp-logs" className="mt-8">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="font-head text-lg font-bold tracking-tight text-zinc-900">Logs de provisioning (TFTP)</h2>
            <p className="text-xs text-zinc-500">Récupérations de config par les téléphones (SEP&lt;MAC&gt;.cnf.xml).</p>
          </div>
          <button
            data-testid="refresh-tftp-logs"
            onClick={loadLogs}
            className="inline-flex items-center gap-1.5 rounded-sm border border-zinc-300 bg-white px-3 py-1.5 text-xs font-medium text-zinc-700 hover:bg-zinc-50"
          >
            <RefreshCw className="h-3.5 w-3.5" /> Rafraîchir
          </button>
        </div>
        <div className="mt-3 overflow-hidden rounded-sm border border-zinc-200 bg-white">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-zinc-200 text-left text-[10px] uppercase tracking-[0.15em] text-zinc-500">
                <th className="px-4 py-2 font-bold">Heure</th>
                <th className="px-4 py-2 font-bold">Source</th>
                <th className="px-4 py-2 font-bold">Fichier</th>
                <th className="px-4 py-2 font-bold">Poste</th>
                <th className="px-4 py-2 font-bold">Hôtel</th>
              </tr>
            </thead>
            <tbody>
              {logs.map((e, i) => (
                <tr key={i} className="border-b border-zinc-100 last:border-0 hover:bg-zinc-50">
                  <td className="px-4 py-2 font-mono text-xs text-zinc-500">{e.ts}</td>
                  <td className="px-4 py-2 font-mono text-xs text-zinc-700">{e.ip}</td>
                  <td className="px-4 py-2 font-mono text-xs text-zinc-700">{e.file}</td>
                  <td className="px-4 py-2 font-mono text-xs font-semibold text-zinc-900">{e.extension || "—"}</td>
                  <td className="px-4 py-2 text-xs text-zinc-700">{e.hotel || "—"}</td>
                </tr>
              ))}
              {logs.length === 0 && (
                <tr>
                  <td colSpan={5} className="px-4 py-8 text-center text-sm text-zinc-400">
                    {logsMsg || "Aucune récupération TFTP enregistrée pour l'instant."}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>

      <AsteriskLogs servers={servers} />
    </div>
  );
}
